import React from 'react';
import Layout from '../../../components/Layout';
import Page from '../../../components/Page';
import fetch from '../../../core/fetch';

/**
 * Trust & Safety Page Route Handler
 * 
 * Uses the new DynamicContent system via GraphQL for i18n.
 * Falls back to StaticPage if DynamicContent has no data.
 * Includes FAQ Schema for SEO.
 */

// Helper to normalize locale to short code
const getShortLocale = (locale) => {
  if (!locale) return 'es';
  return locale.substring(0, 2).toLowerCase();
};

// Helper to get localized field (fallback for StaticPage format)
const getLocalizedField = (data, field, locale) => {
  if (!data) return '';
  const code = getShortLocale(locale);
  if (code === 'es') return data[field] || '';
  const localizedField = `${field}_${code}`;
  return data[localizedField] || data[field] || '';
};

// Query for DynamicContent (new system)
const dynamicContentQuery = `query getDynamicContent($contentType: String!, $contentKey: String!, $locale: String!) {
  getDynamicContent(contentType: $contentType, contentKey: $contentKey, locale: $locale) {
    id
    content
    metaTitle
    metaDescription
    locale
  }
}`;

// Fallback query for StaticPage (legacy system)
const staticPageQuery = `query getEditStaticPage($id: Int!) {
  getEditStaticPage(id: $id) {
    id
    pageName
    content
    content_en
    content_fr
    content_it
    content_pt
    metaTitle
    metaTitle_en
    metaTitle_fr
    metaTitle_it
    metaTitle_pt
    metaDescription
    metaDescription_en
    metaDescription_fr
    metaDescription_it
    metaDescription_pt
  }
}`;

// SEO: FAQPage Schema for Safety page
const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "¿Es seguro alquilar un bote en Navega?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí. Todos los botes son verificados, los pagos son procesados de forma segura, y los dueños pasan por un proceso de verificación de identidad."
      }
    },
    {
      "@type": "Question",
      "name": "¿Qué seguro cubre mi reserva?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cada bote cuenta con su propio seguro de navegación. Adicionalmente, Navega ofrece protección al huésped en caso de problemas con la reserva."
      }
    },
    {
      "@type": "Question",
      "name": "¿Cómo verifico que un bote es confiable?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Revisa las reseñas de otros usuarios, las fotos verificadas, y el perfil del dueño. Los botes con muchas reservas exitosas son más confiables."
      }
    },
    {
      "@type": "Question",
      "name": "¿Qué hago si tengo un problema durante el alquiler?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Contacta inmediatamente al soporte de Navega. Tenemos un equipo disponible para resolver cualquier emergencia o inconveniente."
      }
    },
    {
      "@type": "Question",
      "name": "¿Los capitanes están certificados?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sí, todos los capitanes que ofrecen servicios en Navega deben presentar sus licencias y certificaciones de navegación vigentes."
      }
    }
  ]
};

export default async function action({ locale }) {
  try {
    // First try the new DynamicContent system
    const dynamicResp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: dynamicContentQuery,
        variables: {
          contentType: 'static_page',
          contentKey: 'safety',
          locale: locale || 'es'
        },
      }),
      credentials: 'include',
    });

    const dynamicData = await dynamicResp.json();
    const dynamicContent = dynamicData?.data?.getDynamicContent;

    // If DynamicContent has data, use it
    if (dynamicContent && dynamicContent.content) {
      return {
        title: dynamicContent.metaTitle || 'Trust & Safety',
        description: dynamicContent.metaDescription || '',
        chunk: 'trustAndSafety',
        jsonLd: [faqSchema],
        component: <Layout><Page html={dynamicContent.content} title={dynamicContent.metaTitle} /></Layout>,
      };
    }

    // Fallback to StaticPage (legacy system)
    const staticResp = await fetch('/graphql', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: staticPageQuery,
        variables: { id: 2 },
      }),
      credentials: 'include',
    });

    const staticData = await staticResp.json();
    const page = staticData?.data?.getEditStaticPage;

    if (!page) {
      return undefined;
    }

    // Get localized content from StaticPage
    const title = getLocalizedField(page, 'metaTitle', locale);
    const description = getLocalizedField(page, 'metaDescription', locale);
    const content = getLocalizedField(page, 'content', locale);

    return {
      title: title || 'Trust & Safety',
      description,
      chunk: 'trustAndSafety',
      jsonLd: [faqSchema],
      component: <Layout><Page html={content} title={title} /></Layout>,
    };
  } catch (error) {
    console.error('[Trust & Safety Page] Error loading content:', error);

    return {
      title: 'Trust & Safety',
      description: '',
      chunk: 'trustAndSafety',
      jsonLd: [faqSchema],
      component: <Layout><Page html="<p>Error loading content</p>" title="Trust & Safety" /></Layout>,
    };
  }
}
